import { DndContext, type DragEndEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Box } from '@mui/material';

import { TaskCard } from '~/components/tasks/TaskCard';
import { useStatusGroups, useTaskDragSensors } from '~/components/tasks/taskGroups';
import { previewRowProps, type TaskHover } from '~/components/tasks/taskPreviewRow';
import { STATUS_COLOR } from '~/components/tasks/taskStyle';
import type { TaskDetail, TaskStatus, TaskSummary } from '~/data/generated/model';
import { SURFACE_RADIUS } from '~/theme';

function DraggableCard({
  task,
  onOpen,
  showProperty,
  onHover,
  highlighted,
}: {
  task: TaskSummary;
  onOpen: () => void;
  showProperty: boolean;
  onHover: TaskHover;
  highlighted: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
  });
  const { role: _role, tabIndex: _tabIndex, ...dragAttributes } = attributes;
  // Space on the card opens it; keyboard moves go through the list's drag handle.
  const { onKeyDown: _onKeyDown, ...pointerListeners } = listeners ?? {};
  return (
    <Box
      ref={setNodeRef}
      {...dragAttributes}
      {...pointerListeners}
      {...previewRowProps(task.id, onHover)}
      sx={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 2 : 'auto',
        position: 'relative',
        touchAction: 'none',
      }}
    >
      <TaskCard task={task} onOpen={onOpen} showProperty={showProperty} highlighted={highlighted} />
    </Box>
  );
}

function Column({
  status,
  tasks,
  onOpen,
  showProperty,
  onHover,
  highlightedId,
}: {
  status: TaskStatus;
  tasks: TaskSummary[];
  onOpen: (task: TaskSummary) => void;
  showProperty: boolean;
  onHover: TaskHover;
  highlightedId: string | null;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id });
  const color = STATUS_COLOR[status.color];
  return (
    <Box
      ref={setNodeRef}
      role="region"
      aria-label={`${status.name} column`}
      sx={{
        flex: '0 0 272px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        p: '10px',
        borderRadius: `${SURFACE_RADIUS}px`,
        bgcolor: isOver ? '#e8eefb' : '#f4f6f9',
        border: '1px solid',
        borderColor: isOver ? '#9fb8e8' : 'transparent',
        minHeight: 160,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '2px' }}>
        <Box
          aria-hidden
          sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color.solid }}
        />
        <Box component="h3" sx={{ m: 0, fontSize: 13, fontWeight: 600, flex: 1 }}>
          {status.name}
        </Box>
        <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
          {tasks.length}
        </Box>
      </Box>
      {tasks.map((task) => (
        <DraggableCard
          key={task.id}
          task={task}
          onOpen={() => onOpen(task)}
          showProperty={showProperty}
          onHover={onHover}
          highlighted={task.id === highlightedId}
        />
      ))}
    </Box>
  );
}

export function TaskBoard({
  tasks,
  statuses,
  onOpen,
  showProperty,
  onHover,
  highlightedId = null,
  onCompleted,
}: {
  tasks: TaskSummary[];
  statuses: readonly TaskStatus[];
  onOpen: (task: TaskSummary) => void;
  showProperty: boolean;
  onHover?: TaskHover;
  highlightedId?: string | null;
  onCompleted?: (task: TaskDetail) => void;
}) {
  const { groups, move, moveFailed, dismissMoveFailed } = useStatusGroups(
    tasks,
    statuses,
    onCompleted,
  );
  const sensors = useTaskDragSensors();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) void move(String(active.id), String(over.id));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
      {moveFailed ? (
        <Alert severity="error" onClose={dismissMoveFailed}>
          Couldn&apos;t move the task. Try again.
        </Alert>
      ) : null}
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            overflowX: 'auto',
            pb: '8px',
            alignItems: 'flex-start',
          }}
        >
          {groups.map(({ status, shown }) => (
            <Column
              key={status.id}
              status={status}
              tasks={shown}
              onOpen={onOpen}
              showProperty={showProperty}
              onHover={onHover}
              highlightedId={highlightedId}
            />
          ))}
        </Box>
      </DndContext>
    </Box>
  );
}
